import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { BarChart3, FileDown, Loader2, FileText, CheckCircle2, AlertTriangle, Clock, Calculator } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ISYRaporlar({ ihaleler }) {
  const [selectedIhale, setSelectedIhale] = useState(null);
  const [ozet, setOzet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchOzet = async (ihale) => {
    setSelectedIhale(ihale);
    setLoading(true);
    setOzet(null);
    try {
      const res = await axios.get(`${API}/isy/rapor/${ihale.id}/ozet`, { withCredentials: true });
      setOzet(res.data);
    } catch (e) { toast.error('Özet yüklenemedi'); }
    finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    if (!selectedIhale) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/isy/rapor/${selectedIhale.id}`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ISY_Rapor_${selectedIhale.is_adi?.replace(/\s/g, '_').substring(0, 30)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF indirildi');
    } catch (e) { toast.error('PDF oluşturma hatası'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-4" data-testid="isy-raporlar">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-orange-400" />
        <h2 className="text-lg font-bold text-white">Raporlar</h2>
      </div>

      {/* İhale Seçimi */}
      <div>
        <p className="text-sm text-gray-400 mb-2">Rapor oluşturmak istediğiniz ihaleyi seçin:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(ihaleler || []).map(ih => (
            <Card key={ih.id}
              className={`border cursor-pointer transition-colors ${selectedIhale?.id === ih.id ? 'bg-orange-900/20 border-orange-600/40' : 'bg-gray-900/60 border-gray-700/50 hover:border-orange-500/30'}`}
              onClick={() => fetchOzet(ih)} data-testid={`rapor-select-${ih.id}`}>
              <CardContent className="py-3 px-4">
                <p className="text-sm font-semibold text-white">{ih.is_adi}</p>
                <p className="text-xs text-gray-500 mt-1">Aşama: {ih.mevcut_asama} | IKN: {ih.ikn || '-'}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Rapor Önizleme */}
      {loading && <div className="text-center text-gray-400 py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}

      {ozet && !loading && selectedIhale && (
        <Card className="bg-gray-900/80 border-orange-800/20">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-orange-300 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Rapor Önizleme: {selectedIhale.is_adi}
              </CardTitle>
              <Button onClick={downloadPdf} disabled={downloading} className="bg-orange-600 hover:bg-orange-700 text-white text-xs h-8" data-testid="rapor-download">
                {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileDown className="w-4 h-4 mr-1" />}
                {downloading ? 'Oluşturuluyor...' : 'PDF İndir'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="p-3 bg-gray-800/50 rounded-lg text-center">
                <CheckCircle2 className="w-5 h-5 text-green-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-white">{ozet.sartname_sayisi}</p>
                <p className="text-[10px] text-gray-500">Şartname Maddesi</p>
                <p className="text-xs text-green-400">{ozet.sartname_uygun} uygun</p>
              </div>
              <div className="p-3 bg-gray-800/50 rounded-lg text-center">
                <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-white">{ozet.eksiklik_sayisi}</p>
                <p className="text-[10px] text-gray-500">Eksiklik</p>
                <p className="text-xs text-red-400">{ozet.eksiklik_acik} açık</p>
              </div>
              <div className="p-3 bg-gray-800/50 rounded-lg text-center">
                <Calculator className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-white">{ozet.kesif_sayisi}</p>
                <p className="text-[10px] text-gray-500">Keşif Kalemi</p>
                <p className="text-xs text-amber-400">{ozet.kesif_toplam?.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</p>
              </div>
              <div className="p-3 bg-gray-800/50 rounded-lg text-center">
                <Clock className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-white">{ozet.sure_olay_sayisi}</p>
                <p className="text-[10px] text-gray-500">Süre Olayı</p>
              </div>
            </div>

            <div className="mt-3 p-3 bg-gray-800/30 rounded-lg">
              <p className="text-xs text-gray-400">Rapor İçeriği:</p>
              <ul className="text-xs text-gray-300 mt-1 space-y-0.5 list-disc list-inside">
                <li>İhale Genel Bilgileri</li>
                <li>YİGŞ Uyum Kontrolü ({ozet.sartname_sayisi} madde)</li>
                <li>Eksiklik Listesi ({ozet.eksiklik_sayisi} kayıt)</li>
                <li>Keşif Özeti ({ozet.kesif_sayisi} kalem)</li>
                <li>Süre Olayları ({ozet.sure_olay_sayisi} olay)</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
